// Shared types + per-type config for the agent_node variables sections (SER-5959).
// FE state mirrors the wire shape exactly: agent_tasks.variables (ordered, asked
// on the call) and agent_tasks.extract_only (captured from conversation).
//
// A plain variable is simply asked. Attaching an initial value (known_value)
// makes silent use the default; "Confirm it first" is an opt-in checkbox that
// maps onto confirm_if_known. The FE carries no separate "mode" concept.

export interface AgentTaskVariable {
  _uid: string
  name: string
  instructions: string
  type: string
  params?: Record<string, unknown>
  optional?: boolean
  known_value?: string
  confirm_if_known?: boolean
}

export interface ExtractOnlyVariable {
  _uid: string
  name: string
  instructions: string
}

export interface AgentTasksValue {
  variables: AgentTaskVariable[]
  extract_only: ExtractOnlyVariable[]
  confirm_announcement?: string
  max_restarts?: number
}

// Saved wire shape: getNodeConfig strips default-false flags and empty params,
// so everything except name is optional on the wire. formatConfigData
// re-hydrates this back into AgentTasksValue.
export interface AgentTasksWire {
  variables?: Array<{ name: string } & Partial<Omit<AgentTaskVariable, 'name' | '_uid'>>>
  extract_only?: Array<{ name: string } & Partial<Omit<ExtractOnlyVariable, 'name' | '_uid'>>>
  confirm_announcement?: string
  max_restarts?: number
}

export const TYPE_OPTIONS = [
  { label: 'Text', value: 'text' },
  { label: 'Name', value: 'name' },
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'Choice', value: 'choice' },
  { label: 'Date', value: 'date' },
  { label: 'Currency', value: 'currency' },
  { label: 'Yes/No', value: 'boolean' },
  { label: 'Address', value: 'address' },
  { label: 'Number', value: 'number' },
  { label: 'Date of birth', value: 'dob' },
  { label: 'Keypad digits (DTMF)', value: 'dtmf' },
]

export const typeLabel = (value: string) =>
  TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value

// Derive the default initial-value reference for a variable from its name: the
// same name-form node-var syntax the runner renders for first_response and the
// message/whatsapp from-fields (verified name-form on the wire).
// Prefill the derived ref only when it names a variable the flow actually has;
// otherwise the field starts empty (the placeholder still shows the shape).
export const prefillInitialRef = (name: string, nodeVars: string[]) => {
  const ref = defaultInitialRef(name)
  const bare = ref.replace(/^\{\{|\}\}$/g, '')
  return nodeVars.includes(bare) || nodeVars.includes(ref) ? ref : ''
}

export const defaultInitialRef = (name: string) =>
  `{{Start.http.params.${name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}}}`

// A 4th `requires` element names another param in the same list that this one
// depends on: it stays locked (and off) until its dependency is on. The generic
// checkbox loop reads it; applyAdvancedChange enforces it on every toggle.
type AdvancedParam = [key: string, label: string, defaultValue: boolean, requires?: string]

export const TYPE_ADVANCED: Record<string, AdvancedParam[]> = {
  name: [
    ['first_name', 'First name', true],
    ['last_name', 'Last name', true],
    ['middle_name', 'Middle name', false],
    ['verify_spelling', 'Verify spelling', false],
  ],
  phone: [['vanity', 'Accept vanity numbers', true]],
  text: [['uppercase', 'Uppercase the value', false]],
  choice: [
    ['multiple', 'Allow multiple choices', false],
    ['present_options', 'Read the options aloud', true],
    ['ask_before_update', 'Ask the question first (hold recording until answered)', false],
    ['require_confirmation', 'Confirm the value before recording', false],
    ['confirm_before_complete', 'Re-confirm before completing', false],
  ],
  date: [
    ['accept_past', 'Accept past dates', false],
    ['accept_today', 'Accept today', true],
    ['require_time', 'Require a time of day', false],
  ],
  boolean: [['require_confirmation', 'Confirm the answer before recording', true]],
  currency: [
    ['required_currency', 'Currency required', false],
    ['required_period', 'Period required (per week/month)', false],
  ],
  // No unit_required toggle: the runtime treats units as a group (units +
  // unit_required + default_unit) and there is no units editor here yet. With an
  // empty unit list CollectNumber fails every answer — a bare value hits
  // "unit is required", and a spoken unit hits "Invalid unit; expected one of:
  // none" — so the toggle can only ever break collection. Restore it alongside a
  // units editor plus validation requiring at least one allowed unit.
  number: [
    ['allow_decimal', 'Allow decimals', true],
    ['allow_negative', 'Allow negative values', false],
    ['require_confirmation', 'Confirm the value before recording', true],
    ['ask_before_update', 'Ask the question first (hold recording until answered)', false],
  ],
  dob: [
    ['include_time', 'Also capture a time of day', false],
    // require_time is meaningless without include_time — the runtime cannot
    // require a time it never captures — so it depends on include_time.
    ['require_time', 'Require a time of day', false, 'include_time'],
  ],
}

// Toggle one advanced param and keep dependent params consistent: turning a
// param off clears anything that `requires` it, so params can never persist a
// dependent (e.g. dob require_time) as true while its dependency is false.
export const applyAdvancedChange = (
  type: string,
  params: Record<string, unknown>,
  key: string,
  checked: boolean,
): Record<string, unknown> => {
  const next = { ...params, [key]: checked }
  if (!checked) {
    for (const [depKey, , , requires] of TYPE_ADVANCED[type] ?? []) {
      if (requires === key) {
        next[depKey] = false
      }
    }
  }
  return next
}

export const TYPE_NOTES: Record<string, string> = {
  email: 'Spell-out read-back and mandatory confirmation are built in.',
  phone: 'Digit-grouped read-back and correction handling are built in.',
  address: 'The full address is collected by default; narrow it with the field below.',
  number:
    'Captures a numeric value; decimals are accepted, negatives and units are off unless enabled.',
  dob: 'Captures a date of birth; the time of day is off unless enabled.',
  dtmf: 'The caller enters digits on the keypad; the exact digit count is required below.',
}
